import React, { useState, useEffect } from 'react';
import { 
  X, 
  AlertCircle, 
  Flag, 
  User, 
  MessageSquare, 
  FileText,
  CheckCircle,
  Loader,
  Shield,
  HelpCircle
} from 'lucide-react';
import useReportStore from '../../store/reportStore';
import useAuthStore from '../../store/authStore';
import { toast } from 'react-hot-toast';

const ReportModal = () => {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const {
    showReportModal,
    reportTarget,
    reportReasons,
    isLoading,
    createReport,
    closeReportModal,
    getReportReasons
  } = useReportStore();
  
  const { user } = useAuthStore();

  useEffect(() => {
    if (showReportModal && reportReasons.length === 0) {
      getReportReasons();
    }
  }, [showReportModal]);

  if (!showReportModal || !reportTarget) return null;

  const getTargetInfo = () => {
    const { type, id, data } = reportTarget;
    
    switch (type) {
      case 'post':
        return {
          title: 'Report Post',
          icon: FileText,
          bg: 'from-orange-500 to-orange-600',
          preview: data?.content ? (
            <div className="mt-2 p-3 bg-[#E8ECF5] rounded-xl shadow-neomorph-inset">
              <p className="text-sm text-gray-700 line-clamp-3">{data.content}</p>
              {data.author && (
                <p className="text-xs text-gray-500 mt-1">
                  Posted by: {data.author.firstName} {data.author.lastName}
                </p>
              )}
            </div>
          ) : null,
          targetId: { postId: id }
        };
        
      case 'comment':
        return {
          title: 'Report Comment',
          icon: MessageSquare,
          bg: 'from-purple-500 to-purple-600',
          preview: data?.content ? (
            <div className="mt-2 p-3 bg-[#E8ECF5] rounded-xl shadow-neomorph-inset">
              <p className="text-sm text-gray-700 line-clamp-3">{data.content}</p>
              {data.author && (
                <p className="text-xs text-gray-500 mt-1">
                  Comment by: {data.author.firstName} {data.author.lastName}
                </p>
              )}
            </div>
          ) : null,
          targetId: { commentId: id }
        };
        
      case 'user':
        return {
          title: 'Report User',
          icon: User,
          bg: 'from-blue-500 to-blue-600',
          preview: data?.user ? (
            <div className="mt-2 p-3 bg-[#E8ECF5] rounded-xl shadow-neomorph-inset">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-md flex items-center justify-center">
                  {data.user.avatar ? (
                    <img 
                      src={data.user.avatar} 
                      alt={data.user.firstName}
                      className="h-10 w-10 rounded-xl object-cover"
                    />
                  ) : (
                    <span className="text-white font-semibold">
                      {data.user.firstName[0]}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {data.user.firstName} {data.user.lastName}
                  </p>
                  {data.user.bio && (
                    <p className="text-xs text-gray-500 line-clamp-1">
                      {data.user.bio}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : null,
          targetId: { userId: id }
        };
        
      default:
        return {
          title: 'Report',
          icon: Flag,
          bg: 'from-red-500 to-red-600',
          preview: null,
          targetId: {}
        };
    }
  };

  const targetInfo = getTargetInfo();
  const TargetIcon = targetInfo.icon;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reason) {
      toast.error('Please select a reason for reporting');
      return;
    }

    if (reason === 'OTHER' && description.trim().length < 10) {
      toast.error('Please provide more details for "Other" reason (minimum 10 characters)');
      return;
    }

    setIsSubmitting(true);
    try {
      await createReport({
        ...targetInfo.targetId,
        reason,
        description: description.trim() || null,
      });
      
      setReason('');
      setDescription('');
    } catch (error) {
      console.error('Report submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={closeReportModal}></div>

        <div className="inline-block align-bottom bg-white rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 border border-primary-100/30">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={closeReportModal}
              disabled={isSubmitting}
              className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1.5 shadow-neomorph-inset"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              {/* Header with icon */}
              <div className="flex items-center space-x-3 mb-4">
                <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${targetInfo.bg} shadow-lg flex items-center justify-center`}>
                  <TargetIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center mb-1">
                    <div className="w-1 h-5 bg-gradient-to-b from-red-400 to-red-600 rounded-full mr-2" />
                    <h3 className="text-lg font-semibold text-gray-800">
                      {targetInfo.title}
                    </h3>
                  </div>
                  <p className="text-xs text-gray-500 ml-3">
                    Help us keep the community safe
                  </p>
                </div>
              </div>
              
              {/* Target Preview */}
              {targetInfo.preview && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-600 mb-2">
                    You are reporting:
                  </p>
                  {targetInfo.preview}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Report Reasons */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    What's the issue? <span className="text-red-500">*</span>
                  </label>
                  
                  {isLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="h-10 bg-[#E8ECF5] rounded-lg shadow-neomorph-inset animate-pulse"></div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {reportReasons.map((reasonOption) => (
                        <label
                          key={reasonOption.value}
                          className={`flex items-center p-3 rounded-xl cursor-pointer transition-all border ${
                            reason === reasonOption.value
                              ? 'bg-red-50 border-red-200 shadow-neomorph-inset'
                              : 'bg-white border-gray-200 shadow-neomorph hover:shadow-neomorph-hover'
                          }`}
                        >
                          <input
                            type="radio"
                            name="reason"
                            value={reasonOption.value}
                            checked={reason === reasonOption.value}
                            onChange={(e) => setReason(e.target.value)}
                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                            disabled={isSubmitting}
                          />
                          <span className="ml-3 text-xs text-gray-700">
                            {reasonOption.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Additional Details */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Additional details
                    <span className="text-gray-400 text-xs font-normal ml-1">
                      {reason === 'OTHER' ? '(required for "Other")' : '(optional)'}
                    </span>
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Please provide any additional information that might help us understand the issue..."
                    className="block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/50 min-h-[100px] resize-none"
                    maxLength={1000}
                    disabled={isSubmitting}
                    required={reason === 'OTHER'}
                  />
                  <div className="flex justify-between mt-1.5">
                    <span className="text-xs text-gray-500 bg-[#E8ECF5] px-2 py-0.5 rounded-lg shadow-neomorph-inset">
                      {description.length}/1000
                    </span>
                    {reason === 'OTHER' && description.trim().length < 10 && description.length > 0 && (
                      <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-lg shadow-neomorph-inset">
                        Minimum 10 characters required
                      </span>
                    )}
                  </div>
                </div>

                {/* Important Information */}
                <div className="bg-blue-50 rounded-xl p-4 shadow-neomorph-inset border border-blue-200/50">
                  <div className="flex items-start">
                    <div className="p-1.5 bg-blue-100 rounded-md shadow-neomorph-inset mr-3">
                      <Shield className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xs font-medium text-blue-800 mb-2">Important Information</h4>
                      <ul className="text-xs text-blue-700 space-y-1 list-disc pl-4">
                        <li>Reports are reviewed by our moderation team</li>
                        <li>You will be notified when action is taken</li>
                        <li>False reports may result in account restrictions</li>
                        <li>Your report is anonymous to the reported party</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={closeReportModal}
                    disabled={isSubmitting}
                    className="mt-3 w-full inline-flex justify-center rounded-lg px-4 py-2.5 bg-white shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100 sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !reason || (reason === 'OTHER' && description.trim().length < 10)}
                    className="w-full inline-flex justify-center rounded-lg px-4 py-2.5 bg-gradient-to-r from-red-600 to-red-700 shadow-md shadow-red-500/30 hover:shadow-lg hover:shadow-red-500/40 transition-all text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Flag className="-ml-1 mr-2 h-4 w-4" />
                        Submit Report
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default ReportModal;